// Helper: fall back to static files in /public when R2 is unavailable
async function fetchFromStaticPublic(objectKey) {
  // objectKey is usually "public/..." – strip that prefix for the local path
  let path = String(objectKey || '');
  if (path.startsWith('public/')) {
    path = path.slice('public/'.length);
  }

  // Always go through the local CSV API so we bypass any quirks
  // of the CRA dev server static handler (spaces in paths, etc.).
  const qs = new URLSearchParams({
    path,
    v: String(Date.now()) // cache buster
  }).toString();
  const url = `/api/local-csv?${qs}`;
  console.warn('[fetchR2CsvText] Falling back to local CSV API:', url);

  const res = await fetch(url, { 
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });
  if (!res.ok) {
    console.error('[fetchR2CsvText] Local static fetch failed:', res.status, 'for', url);
    throw new Error(`Local static fetch failed: ${res.status} for ${url}`);
  }

  const text = await res.text();
  console.log('[fetchR2CsvText] Loaded', text.length, 'bytes from static asset:', url);
  return text;
}

export async function fetchR2CsvText(objectKey) {
  // Ensure objectKey is a string
  if (typeof objectKey !== 'string') {
    console.error('[fetchR2CsvText] Invalid objectKey type:', typeof objectKey, objectKey);
    throw new Error(`fetchR2CsvText expects a string, got ${typeof objectKey}`);
  }

  // In local development (localhost), always prefer the on-disk CSVs
  // generated by Data_UCR so chart updates appear immediately without
  // having to sync R2. This keeps dev behavior aligned with recent runs.
  try {
    if (typeof window !== 'undefined') {
      const host = window.location && window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        console.log('[fetchR2CsvText] Localhost detected – using static CSV for:', objectKey);
        return await fetchFromStaticPublic(objectKey);
      }
    }
  } catch {
    // If window is not available or any error occurs, fall through to R2 logic.
  }

  const qs = new URLSearchParams({ key: objectKey }).toString();

  try {
    console.log('[fetchR2CsvText] Requesting key from R2:', objectKey);
    const res = await fetch(`/api/r2-get?${qs}`, { credentials: 'include' });

    if (!res.ok) {
      const msg = await res.text().catch(() => '');
      console.error('[fetchR2CsvText] R2 API error:', res.status, msg);

      // Provide specific error messages for common issues
      if (msg.includes('missing R2_BUCKET_NAME')) {
        console.warn('[fetchR2CsvText] R2_BUCKET_NAME missing – using static fallback');
        return await fetchFromStaticPublic(objectKey);
      }

      if (msg.includes('unauthorized') || res.status === 401 || res.status === 403) {
        console.warn('[fetchR2CsvText] R2 unauthorized/forbidden – using static fallback');
        return await fetchFromStaticPublic(objectKey);
      }

      // For other R2 API failures, also fall back to static assets
      console.warn('[fetchR2CsvText] R2 API failed, falling back to static asset');
      return await fetchFromStaticPublic(objectKey);
    }

    const { url } = await res.json();
    console.log('[fetchR2CsvText] Got signed URL for:', objectKey);

    const csvRes = await fetch(url);

    if (!csvRes.ok) {
      console.error('[fetchR2CsvText] Failed to fetch from signed URL:', csvRes.status, 'for key:', objectKey);

      if (csvRes.status === 404) {
        console.warn('[fetchR2CsvText] R2 404 – falling back to static asset');
        return await fetchFromStaticPublic(objectKey);
      }

      console.warn('[fetchR2CsvText] R2 signed URL fetch failed, falling back to static asset');
      return await fetchFromStaticPublic(objectKey);
    }

    const text = await csvRes.text();
    console.log('[fetchR2CsvText] Successfully loaded', text.length, 'bytes from R2 for:', objectKey);
    return text;

  } catch (error) {
    console.error('[fetchR2CsvText] Complete error for key:', objectKey);
    console.error('[fetchR2CsvText] Error details:', error);

    // Final safety net: if anything in the R2 path throws, try static files
    try {
      return await fetchFromStaticPublic(objectKey);
    } catch (fallbackError) {
      console.error('[fetchR2CsvText] Static fallback also failed for key:', objectKey);
      console.error('[fetchR2CsvText] Fallback error details:', fallbackError);
      throw fallbackError;
    }
  }
}

